import { Button, Menu, MenuItem, Stack } from '@mui/material'
import React from 'react'

import Icon from 'components/Icon'
import { useGraphEditorContext } from 'components/scenarios/concurrent/GraphEditorContext'
import {
  type GraphNodePath,
  serializeNodePathToId
} from 'components/scenarios/utils/graph'

export const BranchActionsMenu: React.FC<{
  branchNodePath: GraphNodePath
  onMoveBranchDown?: (path: GraphNodePath) => void
  onMoveBranchUp?: (path: GraphNodePath) => void
}> = ({ branchNodePath, onMoveBranchDown, onMoveBranchUp }) => {
  const pathId = serializeNodePathToId(branchNodePath)

  const { handleAddBranchAfter, handleAddBranchBefore, handleDeleteBranch } =
    useGraphEditorContext()
  const [branchMenuAnchorEl, setBranchMenuAnchorEl] =
    React.useState<null | HTMLElement>(null)
  const isAddMenuOpen = Boolean(branchMenuAnchorEl)
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setBranchMenuAnchorEl(event.currentTarget)
    return false
  }
  const handleCloseMenu = () => {
    setBranchMenuAnchorEl(null)
  }
  const menuButtonId = `branch-menu-button-${pathId}`
  const menuId = `branch-menu-${pathId}`

  return (
    <Stack direction="row" spacing={2}>
      <Button
        aria-controls={isAddMenuOpen ? menuId : undefined}
        aria-expanded={isAddMenuOpen ? 'true' : undefined}
        aria-haspopup="true"
        aria-label="Insert Branch"
        data-testid={`branch-actions-menu-button-${pathId}`}
        endIcon={<Icon icon="solidCaretDown" />}
        id={menuButtonId}
        size="small"
        variant="outlined"
        onClick={handleOpenMenu}
      >
        Edit Branch
      </Button>

      <Menu
        anchorEl={branchMenuAnchorEl}
        id={menuId}
        MenuListProps={{
          'aria-labelledby': menuButtonId
        }}
        open={isAddMenuOpen}
        onClose={handleCloseMenu}
      >
        <MenuItem
          data-testid={`branch-move-up-${pathId}`}
          disabled={!onMoveBranchUp}
          onClick={
            onMoveBranchUp
              ? () => {
                  handleCloseMenu()
                  onMoveBranchUp(branchNodePath)
                }
              : undefined
          }
        >
          Move Up
        </MenuItem>
        <MenuItem
          data-testid={`branch-move-down-${pathId}`}
          disabled={!onMoveBranchDown}
          onClick={
            onMoveBranchDown
              ? () => {
                  handleCloseMenu()
                  onMoveBranchDown(branchNodePath)
                }
              : undefined
          }
        >
          Move Down
        </MenuItem>
        <MenuItem
          data-testid={`branch-delete-${pathId}`}
          onClick={() => {
            handleCloseMenu()
            handleDeleteBranch(branchNodePath)
          }}
        >
          Delete Branch
        </MenuItem>
        <MenuItem
          data-testid={`branch-prepend-${pathId}`}
          disabled={!handleAddBranchBefore}
          onClick={
            handleAddBranchBefore
              ? () => {
                  handleCloseMenu()
                  handleAddBranchBefore(branchNodePath)
                }
              : undefined
          }
        >
          Prepend Branch
        </MenuItem>
        <MenuItem
          data-testid={`branch-append-${pathId}`}
          disabled={!handleAddBranchAfter}
          onClick={
            handleAddBranchAfter
              ? () => {
                  handleCloseMenu()
                  handleAddBranchAfter(branchNodePath)
                }
              : undefined
          }
        >
          Append Branch
        </MenuItem>
      </Menu>
    </Stack>
  )
}
